import { useCallback, useMemo, useEffect } from 'react';

import { useHistory } from 'react-router-dom';

import { CampaignChannels } from 'domains/campaigns/types';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import AutocompleteContent from 'domains/core/components/AutocompleteContent';
import { ContentItem } from 'domains/core/components/AutocompleteContent/AutocompleteContent';
import { URLPaths } from 'models/enums';

import { useContentSelection } from '../hooks/useContentSelection';

export type ContentSupportedChannels = Extract<
    CampaignChannels,
    CampaignChannels.EMAIL | CampaignChannels.SMS | CampaignChannels.DIRECT_MAIL
>;

type ContentSelectorProps = {
    contentId: number | null;
    contentList: ContentItem[];
    contentType: ContentSupportedChannels;
    onContentChange: (contentId: number | null, contentName?: string) => void;
    isLoading?: boolean;
    isPreviewVisible?: boolean;
    onPreviewChange?: () => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const CREATE_NEW_CONTENT_CONFIG = {
    [CampaignChannels.DIRECT_MAIL]: {
        label: 'Create new direct mail',
        createPath: `${URLPaths.CONTENT_CREATE_DIRECT_MAIL}?id=default`,
    },
    [CampaignChannels.EMAIL]: {
        label: 'Create new email',
        createPath: `${URLPaths.CONTENT_CREATE_EMAIL}?id=default`,
    },
    [CampaignChannels.SMS]: {
        label: 'Create new SMS',
        createPath: `${URLPaths.CONTENT_CREATE_SMS}?id=default`,
    },
};

const ContentSelector = ({
    contentId,
    contentList,
    contentType,
    onContentChange,
    isLoading = false,
    isPreviewVisible,
    onPreviewChange,
    onContentUnavailableChange,
}: ContentSelectorProps) => {
    const history = useHistory();

    const {
        contentInputValue,
        selectedOption,
        isContentUnavailable,
        unavailableContentId,
        handleContentSelection,
        handleInputChange,
    } = useContentSelection({
        contentId,
        contentList,
        onContentChange,
    });

    // Notify parent when content unavailable state changes
    useEffect(() => {
        onContentUnavailableChange?.(isContentUnavailable, unavailableContentId);
    }, [isContentUnavailable, unavailableContentId, onContentUnavailableChange]);

    const options = useMemo<AutocompleteOption<ContentItem>[]>(() => {
        const createNewOption = {
            index: 0,
            label: CREATE_NEW_CONTENT_CONFIG[contentType].label,
            value: null as any,
        };

        const contentOptions = contentList.map((item, index) => ({
            index: index + 1,
            label: item.name,
            value: item,
        }));

        return [createNewOption, ...contentOptions];
    }, [contentType, contentList]);

    const handleCreateNew = useCallback(() => {
        history.push(CREATE_NEW_CONTENT_CONFIG[contentType].createPath);
    }, [contentType, history]);

    return (
        <AutocompleteContent
            options={options}
            value={selectedOption}
            onChange={handleContentSelection}
            onInputChange={handleInputChange}
            contentType={contentType}
            isLoading={isLoading}
            isPreviewVisible={isPreviewVisible}
            selectedContentId={contentId}
            textfieldInput={contentInputValue}
            onContentPreviewClick={onPreviewChange}
            onCreateNew={handleCreateNew}
            isContentUnavailable={isContentUnavailable}
        />
    );
};

export default ContentSelector;
